import { gql, useApolloClient, useMutation, useQuery } from "@apollo/client";
import React, { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { Episode } from "../../components/episode";
import { CATEGORY_FRAGMENT, EPISODE_FRAGMENT, PODCAST_FRAGMENT, REVIEW_FRAGMENT } from "../../fragments";
import { podcastQuery, podcastQueryVariables } from "../../__generated__/podcastQuery";
import { timeSince } from "../../util/utility";
import { Helmet } from "react-helmet-async";
import { SmallButton } from "../../components/small-button";
import { toggleSubscribeMutation, toggleSubscribeMutationVariables } from "../../__generated__/toggleSubscribeMutation";
import { useMe } from "../../hooks/useMe";
import { useForm } from "react-hook-form";
import { Button } from "../../components/button";
import { createReview, createReviewVariables } from "../../__generated__/createReview";

export const PODCAST_QUERY = gql`
  query podcastQuery ($input: PodcastSearchInput!) {
    getPodcast (input: $input) {
      ok
      error
      podcast {
        ...PodcastParts
        category {
        ...CategoryParts
        }
        episodes {
        ...EpisodeParts
        }
        reviews {
        ...ReviewParts
        }
        creator {
            email
        }
      }
    }
  }
  
  ${PODCAST_FRAGMENT}
  ${CATEGORY_FRAGMENT}
  ${EPISODE_FRAGMENT}
  ${REVIEW_FRAGMENT}
`;

const SUBSCRIBE_MUTATION = gql`
  mutation toggleSubscribeMutation ($input: ToggleSubscribeInput!) {
    toggleSubscribe (input: $input) {
      ok
      error
    }
  }
`;

const REVIEW_MUTATION = gql`
  mutation createReview ($input: CreateReviewInput!) {
    createReview (input: $input) {
      ok
      error
      id
    }
  }
`;

interface IFormProps {
    reviewText: string;
    reviewTitle: string;
}

export const Podcast = () => {
    const client = useApolloClient();
    const { data: userData } = useMe();
    const history = useHistory();
    let { id: podcastId } = useParams();
    const [uploading, setUploading] = useState(false);
    const [subscribeState, setSubscribeState] = useState("");
    const { register, getValues, formState, handleSubmit, reset } = useForm<IFormProps>({
        mode: "onChange",
    });

    const onCompleted = (d: getPodcast) => {
        const {
            getPodcast: { ok, id },
        } = d;
        if (ok) {
            setSubscribeState(IsSubscribed());
        }
    };

    const onReviewCompleted = async (d: createReview) => {
        const {
            createReview: { ok, id },
        } = d;
        if (ok) {
            const { reviewTitle, reviewText } = getValues();
            setUploading(false);
            alert("review success!");
            reset();
            const queryResult = client.readQuery({
                query: PODCAST_QUERY,
                variables: {
                    input: {
                        id: Number(podcastId),
                    },
                },
            });

            client.writeQuery({
                query: PODCAST_QUERY,
                variables: {
                    input: {
                        id: Number(id),
                    },
                },
                data: {
                    getPodcast: {
                        ...queryResult.getPodcast,
                        podcast: {
                            ...queryResult.getPodcast.podcast,
                            reviews: [
                                ...queryResult.getPodcast.podcast.reviews,
                                {
                                    createdAt: Date.now(),
                                    id,
                                    text: reviewText,
                                    title: reviewTitle,
                                    __typename: "Review",
                                }],
                        },
                    },
                },
            });
            // history.push("/");
            // history.go(0);
        }
    };

    const { data, loading } = useQuery<
        podcastQuery,
        podcastQueryVariables
    >(PODCAST_QUERY, {
        variables: {
            input: {
                id: Number(podcastId),
            },
        },
        onCompleted,
    });

    const [toggleSubscribe, { }] = useMutation<
        toggleSubscribeMutation,
        toggleSubscribeMutationVariables
    >(SUBSCRIBE_MUTATION, {
        // onCompleted,
    });

    const [createReview, { }] = useMutation<
        createReview,
        createReviewVariables
    >(REVIEW_MUTATION, {
        onCompleted: onReviewCompleted,
    });

    const IsSubscribed = () => {
        const subsArr = data?.getPodcast.podcast?.subscribers;
        let result = "Subscribe";

        for (let i = 0; i < subsArr?.length; i++) {
            if (subsArr[0].email === userData?.me.email) {
                result = "Unsubscribe";
                break;
            }
        }

        return result;
    }

    const onSubscribe = async () => {
        if (subscribeState === "Subscribe") {
            setSubscribeState("Unsubscribe");

        } else {
            setSubscribeState("Subscribe");
        }

        toggleSubscribe({
            variables: {
                input: {
                    podcastId: Number(data?.getPodcast.podcast?.id),
                },
            },
        });
    }

    const onSubmit = () => {
        const { reviewTitle, reviewText } = getValues();
        setUploading(true);

        createReview({
            variables: {
                input: {
                    podcastId: Number(data?.getPodcast.podcast?.id),
                    title: reviewTitle,
                    text: reviewText,
                },
            },
        });
    }

    return (
        <div className="pb-96">
            <Helmet>
                <title>Podcast | KUDOK</title>
            </Helmet>
            <div className="bg-gray-800 w-full py-10 flex bg-no-repeat bg-cover" style={{ backgroundImage: `url(${data?.getPodcast.podcast?.coverImg || '/images/podcast.jpeg'})` }}>
                <div className="flex flex-col text-white p-5 bg-gradient-to-r from-black">
                    <div className="flex flex-row m-5">
                        <div className="flex flex-col">
                            <h3 className="text-5xl">{data?.getPodcast.podcast?.title}</h3>
                            <span className="py-2 text-xl border-gray-400">
                                {data?.getPodcast.podcast?.creator.email}
                            </span>
                            <span className="py-2 text-xs border-gray-400">
                                {timeSince(data?.getPodcast.podcast?.createdAt)}
                            </span>
                        </div>
                    </div>
                    <div className="flex flex-row m-5">
                        <h3 className="p-2 text-xl bg-cover bg-purple-900 rounded-lg">
                            Category 💫 : {data?.getPodcast.podcast?.category?.name}
                        </h3>
                        <h3 className="ml-5 p-2 text-xl bg-purple-900 rounded-lg">
                            Rating 💯 : {data?.getPodcast.podcast?.rating}
                        </h3>
                        <h3 className="ml-5 p-2 text-xl bg-purple-900 rounded-lg">
                            Listeners 👩🏻 : {data?.getPodcast.podcast?.subscribers?.length}
                        </h3>
                    </div>
                    <div className="p-5">
                        {data?.getPodcast.podcast?.description}
                    </div>
                    <div className="flex flex-row">
                        <SmallButton style="ml-5" actionText={subscribeState} onClick={onSubscribe} />
                    </div>
                </div>
            </div>
            <h3 className="py-5 pl-5 text-xl bg-purple-500">
                {data?.getPodcast.podcast?.episodes?.length} Episodes 🎬
            </h3>
            {!loading && (
                <div className="max-w-screen-2xl mx-auto mt-8 pb-20">
                    <div className="grid grid-cols-1 gap-x-5 gap-y-4">
                        {data?.getPodcast.podcast?.episodes?.map((episode) => (
                            <Episode
                                id={episode.id + ""}
                                title={episode.title}
                                description={episode.description}
                                createdAt={episode.createdAt}
                                state={episode}
                            />
                        ))}
                    </div>
                </div>
            )}
            <h3 className="py-5 pl-5 text-xl bg-purple-500">
                {data?.getPodcast.podcast?.reviews?.length} Reviews 📝
            </h3>
            <div className="max-w-screen-2xl mx-auto mt-8 pb-20">
                <div className="grid grid-cols-1 gap-x-5 gap-y-4">
                    {data?.getPodcast.podcast?.reviews?.map((review) => (
                        <div key={review.id} className="flex flex-col m-3 gap-3 pb-5 border-b border-gray-40">
                            <div className="flex flex-row justify-between">
                                <div className="text-purple-500 font-bold">{review.title}</div>
                                <span>{timeSince(review.createdAt)}</span>
                            </div>

                            <div>
                                {/* <span className="text-purple">{review.creator?.email}</span> */}
                                <div>{review.text}</div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
            <div>
                <h3 className="py-5 pl-5 text-xl bg-purple-500">
                    Create Review
                </h3>
                <form
                    onSubmit={handleSubmit(onSubmit)}
                    className="flex flex-col gap-w-full m-5 text-black gap-3"
                >
                    <input
                        className=" focus:ring-2  focus:ring-blue-600 focus:ring-opacity-90 focus:outline-none py-3 px-5 rounded-lg"
                        type="text"
                        placeholder="Title"
                        {...register("reviewTitle", {
                            required: "Title is required",
                        })}
                    />
                    <textarea placeholder="Text" className="w-full focus:ring-2  focus:ring-blue-600 focus:ring-opacity-90 focus:outline-none py-3 px-5 rounded-lg" rows="4" {...register("reviewText", {
                        required: "Text is required",
                    })}></textarea>
                    <Button
                        style=""
                        loading={uploading}
                        canClick={formState.isValid}
                        actionText="Submit"
                    />
                </form>
            </div>
        </div>
    );
};